// Escalation level is DERIVED from time, not stored/forwarded. A complaint has
// a single reference time (when it entered In Progress / was dispatched — the
// 72h clock start). The current level is a pure function of how many hours have
// elapsed since then, against cumulative thresholds. This is computed the same
// way for past and future data, so nothing needs migrating.

// Display-only status relabel (DB still stores "Closed").
import { statusLabel } from './displayText';

const readHours = (key, fallback) => {
  const v = parseFloat(process.env[key]);
  return Number.isFinite(v) && v > 0 ? v : fallback;
};

// Cumulative hours since the reference at which each level begins. Three levels
// only: DCF -> CF -> CCF. CCF (Chief) is the terminal level ("Super Escalated")
// — there is NO Administration level. Must mirror the backend values.
const _DCF_H = readHours('REACT_APP_ESCALATE_DCF_HOURS', 72);
const _CF_H = readHours('REACT_APP_ESCALATE_CF_HOURS', 120);
const _CCF_H = readHours('REACT_APP_ESCALATE_CCF_HOURS', 168);
export const ESC_THRESHOLDS = {
  DCF: _DCF_H,
  Conservator: _CF_H,
  Chief: _CCF_H,
};

export const LEVEL_CODE = {
  DCF: 'DCF',
  Conservator: 'CF',
  Chief: 'CCF',
};

export const LEVEL_LABEL = {
  DCF: 'DCF (Division)',
  Conservator: 'Conservator (CF)',
  Chief: 'Chief Conservator (CCF)',
};

export const LEVEL_ORDER = ['DCF', 'Conservator', 'Chief'];

// Best-effort level extraction from a remark (legacy history rows / fallbacks).
export const parseEscalationLevelFromRemark = (remark = '') => {
  const r = String(remark).toLowerCase();
  // Legacy "administration" rows collapse onto the terminal Chief level.
  if (r.includes('administration')) return 'Chief';
  if (r.includes('chief')) return 'Chief';
  if (r.includes('conservator')) return 'Conservator';
  if (r.includes('dcf')) return 'DCF';
  return null;
};

// The single anchor time. The escalation clock starts at DISPATCH (entering
// In Progress), NEVER at creation — a complaint can sit "Open" for days before
// being dispatched, so anchoring to time_of_call / createdAt would inflate the
// level of older complaints. Order: explicit referenceAt -> dispatched_time ->
// In-Progress history row -> reconstructed from the Escalated moment.
export const escalationReferenceTime = (complaint) => {
  if (!complaint) return null;
  const pick = (v) => (v ? new Date(v) : null);
  if (complaint.escalation?.referenceAt) return pick(complaint.escalation.referenceAt);
  const disp = pick(complaint.dispatched_time);
  if (disp) return disp;
  const ip = (complaint.statusHistory || []).find((e) => e.status === 'In Progress');
  if (ip?.changedAt) return pick(ip.changedAt);
  const escRows = (complaint.statusHistory || []).filter((e) => e.status === 'Escalated');
  const esc = escRows[escRows.length - 1];
  if (esc?.changedAt) return new Date(new Date(esc.changedAt).getTime() - ESC_THRESHOLDS.DCF * 3.6e6);
  return null;
};

export const elapsedHoursSinceRef = (complaint) => {
  const ref = escalationReferenceTime(complaint);
  if (!ref || Number.isNaN(ref.getTime())) return null;
  return (Date.now() - ref.getTime()) / 3.6e6;
};

// Derive the level from an elapsed-hours value alone.
export const levelForElapsedHours = (h) => {
  if (h == null) return null;
  if (h >= ESC_THRESHOLDS.Chief) return 'Chief'; // terminal — "Super Escalated"
  if (h >= ESC_THRESHOLDS.Conservator) return 'Conservator';
  return 'DCF'; // it is Escalated, so at least DCF
};

// Current derived level of an escalated complaint (null if not escalated).
export const currentEscalationLevel = (complaint) => {
  if (!complaint || complaint.status !== 'Escalated') return null;
  const h = elapsedHoursSinceRef(complaint);
  if (h == null) return 'DCF';
  return levelForElapsedHours(h);
};

// Same, but from a plain reference time + status (e.g. map payloads).
export const levelFromReference = (status, referenceAt) => {
  if (status !== 'Escalated' || !referenceAt) return null;
  const ref = new Date(referenceAt);
  if (Number.isNaN(ref.getTime())) return 'DCF';
  return levelForElapsedHours((Date.now() - ref.getTime()) / 3.6e6);
};

// Remark for each derived step — states the REAL threshold (from env), so the
// timeline can never show a stale/wrong number (e.g. an old "48 hours" baked
// into historic statusHistory).
export const forwardRemark = (level) => {
  const h = Math.round(ESC_THRESHOLDS[level]);
  switch (level) {
    case 'DCF':
      return `Escalated at DCF level after ${h} hours without closure.`;
    case 'Conservator':
      return `Automatically forwarded to Conservator (CF) after ${h} hours without closure.`;
    case 'Chief':
      return `Super Escalated to Chief Conservator (CCF) after ${h} hours without closure.`;
    default:
      return '';
  }
};

// Every level the complaint has reached, DERIVED THE SAME WAY as the badge so
// the timeline and the datatable can never contradict. An escalated complaint is
// always at least DCF, so DCF is always included; higher levels are added up to
// the current derived level. The DCF step is timed to the real first-escalation
// moment (its statusHistory row) so it never appears in the future.
export const escalationStepsCrossed = (complaint) => {
  if (!complaint || complaint.status !== 'Escalated') return [];
  const ref = escalationReferenceTime(complaint);
  const current = currentEscalationLevel(complaint) || 'DCF';
  const uptoIdx = LEVEL_ORDER.indexOf(current);
  const escRow = (complaint.statusHistory || []).find((e) => e.status === 'Escalated');
  const escAt = escRow?.changedAt ? new Date(escRow.changedAt) : null;
  return LEVEL_ORDER.slice(0, uptoIdx + 1).map((lvl) => {
    // Every step (including DCF) is computed as reference + its cumulative
    // threshold, so DCF lands at exactly ESCALATE_DCF_HOURS after dispatch —
    // consistent with CF/CCF, not the raw stored escalation time.
    let at = ref ? new Date(ref.getTime() + ESC_THRESHOLDS[lvl] * 3.6e6) : escAt;
    // Only anomalous case: status is Escalated but < DCF_HOURS have elapsed, so
    // the derived time would be in the future — clamp to the real escalation
    // moment (or now) so nothing shows a future date.
    if (at && at.getTime() > Date.now()) {
      at = escAt && escAt.getTime() <= Date.now() ? escAt : new Date();
    }
    return { level: lvl, at, remark: forwardRemark(lvl) };
  });
};

// A complaint that has reached the terminal Chief (CCF) level is "Super Escalated".
export const isSuperEscalated = (complaint) => currentEscalationLevel(complaint) === 'Chief';

// "Escalated · CF" for an escalated complaint, otherwise the plain status.
export const statusWithLevel = (complaint) => {
  if (!complaint) return '';
  if (complaint.status === 'Escalated') {
    const lvl = currentEscalationLevel(complaint);
    return lvl ? `Escalated · ${LEVEL_CODE[lvl] || lvl}` : 'Escalated';
  }
  return statusLabel(complaint.status);
};

// From a plain status + level pair (level already derived).
export const labelForStatus = (status, level) => {
  if (status === 'Escalated' && level) return `Escalated · ${LEVEL_CODE[level] || level}`;
  return statusLabel(status);
};
